import { EntityEventDto, EntityEventType, HandledResponse } from '@/api/generator';
import { injectStrict } from '@/utils/injectStrict';
import { useHubSubscription } from '@/utils/signalR';
import { HubConnectionBuilder, HubConnection } from '@microsoft/signalr';
import { ref, InjectionKey } from 'vue';
import { createEventHook, until, type EventHookOn } from '@vueuse/core';

export const EntityEventHubKey: InjectionKey<HubConnection> = Symbol('EntityEventHubKey');

export function getEntityEventHubConnectionSignalR() {
  const connection = new HubConnectionBuilder()
    .withUrl('/entityEventHub')
    .withAutomaticReconnect([0, 2000, 10000, 30000])
    .build();
  return connection;
}

function executeAndAwaitEvent<T extends (...args: any[]) => Promise<any>>(
  onJobEvent: EventHookOn<EntityEventType>,
  call: T,
  awaitedEvent: EntityEventType,
  timeout = 10 * 1000,
) {
  return async (
    ...args: Parameters<typeof call>
  ): Promise<HandledResponse<Awaited<ReturnType<T>>['result']>> => {
    const receivedEvent = ref(false);

    function updateEvent(event: EntityEventType) {
      receivedEvent.value = event === awaitedEvent;
    }

    const { off: stopListening } = onJobEvent(updateEvent);

    const response = await call(...args);

    if (response.success) {
      await until(receivedEvent).toBe(true, { timeout });
    }

    stopListening();

    return response;
  };
}

export function useEntityEventStream() {
  const connection = injectStrict(EntityEventHubKey, getEntityEventHubConnectionSignalR());

  const entityEventHook = createEventHook<EntityEventDto>();

  useHubSubscription<EntityEventDto>({
    connection,
    event: 'EntityUpdated',
    onMessage: (value) => {
      entityEventHook.trigger(value).catch((error) => {
        console.error('SignalR EntityUpdated: listener failed', error);
      });
    },
    subscribe: () => connection.invoke('RegisterToEvents'),
    unsubscribe: () => connection.invoke('UnRegister'),
  });

  function createEntityEventHookForJob(id: string) {
    const jobEventHookForJob = createEventHook<EntityEventType>();

    entityEventHook.on((entityEvent) => {
      if (entityEvent.entityId === id && entityEvent.event) {
        jobEventHookForJob.trigger(entityEvent.event);
      }
    });

    function executeAndAwaitEventForJob<T extends (...args: any[]) => Promise<any>>(
      call: T,
      awaitedEvent: EntityEventType,
      timeout?: number,
    ) {
      return executeAndAwaitEvent(jobEventHookForJob.on, call, awaitedEvent, timeout);
    }

    return {
      on: jobEventHookForJob.on,
      executeAndAwaitEvent: executeAndAwaitEventForJob,
    };
  }

  return {
    onEntityEvent: entityEventHook.on,
    createEntityEventHookForJob,
  };
}
